import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import ProjectSwitcher from '../components/layout/ProjectSwitcher';
import { clearAuthSession, getRefreshToken, getStoredUser } from '../utils/tokenStorage';
import { logout as logoutRequest } from '../api/auth';

const DashboardLayout = ({ title = 'Dashboard', children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Close sidebar on desktop by default, open on mobile toggle
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true); // Open on desktop
      } else {
        setIsSidebarOpen(false); // Closed on mobile by default
      }
    };

    // Set initial state
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Force users flagged with mustChangePassword into the change password flow
  useEffect(() => {
    if (user?.mustChangePassword && location.pathname !== '/change-password') {
      navigate('/change-password', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const displayName = useMemo(() => {
    if (!user) return 'Signed-in user';

    if (user.fullName) return user.fullName;

    const names = [user.firstName, user.lastName].filter(Boolean);
    return names.length > 0 ? names.join(' ') : user.email || 'Signed-in user';
  }, [user]);

  const displayEmail = user?.email || '';

  const initials = useMemo(() => {
    if (!user) return 'U';

    const source = user.fullName || `${user.firstName || ''} ${user.lastName || ''}` || user.email || 'User';
    return source
      .trim()
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || 'U';
  }, [user]);

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);

    try {
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        await logoutRequest({ refreshToken });
      } else {
        await logoutRequest();
      }
    } catch (error) {
      // Non-blocking; we still clear local session on failure
      console.warn('Logout failed, clearing local session instead:', error);
    } finally {
      clearAuthSession();
      setUser(null);
      setIsLoggingOut(false);
      // Login route is "/" (handled by HashRouter), so navigate there
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-100 text-slate-900">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <main className="flex-1 overflow-hidden">
        <header className="border-b border-slate-200 bg-white px-4 py-6 sm:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                type="button"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden rounded-md p-2 text-slate-700 hover:bg-slate-100 transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="Toggle sidebar"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {isSidebarOpen ? (
                    <path d="M18 6L6 18M6 6l12 12" />
                  ) : (
                    <>
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </>
                  )}
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-primary">{title}</h1>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Enterprise Resource Planning</p>
              </div>
            </div>

            <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:gap-6">
              <ProjectSwitcher />

              <div className="flex items-center gap-4">
                <span className="hidden text-right sm:block">
                  <span className="block text-sm font-semibold text-slate-700">{displayName}</span>
                  {displayEmail ? <span className="block text-xs text-slate-400">{displayEmail}</span> : null}
                </span>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold uppercase text-primary">
                  {initials}
                </span>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-md border border-transparent bg-primary px-3 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? 'Signing out…' : 'Logout'}
                </button>
              </div>
            </div>
          </div>
        </header>
        <section className="h-[calc(100vh-112px)] overflow-y-auto px-4 py-8 sm:px-8">
          {children}
        </section>
      </main>
    </div>
  );
};

export default DashboardLayout;

